import { cva, type VariantProps } from "class-variance-authority";
import { createElement, forwardRef, type HTMLAttributes } from "react";
import { cn } from "@/lib/utils";

/**
 * Tarjeta plana (nivel 0 de elevacion: borde, sin sombra). Sobre fondo blanco
 * puro una sombra de 1px solo agrega ruido bajo cada tarjeta de una pagina con
 * seis; las 85 tarjetas a mano ya la habian sacado.
 *
 * - `variant`: la superficie (default, atenuada para resumenes, punteada para
 *   "falta algo").
 * - `tono`: tinte semantico del contenedor entero (panel de exito, error).
 * - `acento`: barra lateral izquierda. SOLO significa moneda (o `warning` en
 *   "sin retirar"); nunca el signo de un importe, ver MASTER seccion 3.
 * - `padding`: `none` para envolver una <Table> o una lista `divide-y`,
 *   `compacto` para KPIs y tarjetas de lista, `normal` para secciones.
 */
const cardVariants = cva("rounded-lg border text-card-foreground", {
  variants: {
    variant: {
      default: "border-border bg-card",
      muted: "border-border bg-muted/50",
      dashed: "border-dashed border-input bg-card",
    },
    tono: {
      none: "",
      success: "border-success/40 bg-success/10",
      warning: "border-warning/40 bg-warning/10",
      destructive: "border-destructive/40 bg-destructive/10",
      info: "border-info/40 bg-info/10",
    },
    acento: {
      none: "",
      ARS: "border-l-2 border-l-ars",
      USD: "border-l-2 border-l-usd",
      warning: "border-l-2 border-l-warning",
    },
    padding: {
      none: "",
      compacto: "px-4 py-3",
      normal: "p-4",
    },
  },
  // `padding` arranca en `none` y no en `normal` como pide la spec: los 45
  // `<Card className="...">` que hay hoy traen su propio padding (o ninguno,
  // cuando envuelven una tabla con `overflow-hidden`). Cuando la fase 2 los
  // migre a `padding=`, este default pasa a `normal`.
  defaultVariants: { variant: "default", tono: "none", acento: "none", padding: "none" },
});

export interface CardProps extends HTMLAttributes<HTMLElement>, VariantProps<typeof cardVariants> {
  as?: "div" | "section" | "article";
}

export const Card = forwardRef<HTMLElement, CardProps>(
  ({ as = "div", className, variant, tono, acento, padding, ...props }, ref) =>
    createElement(as, {
      ref,
      className: cn(cardVariants({ variant, tono, acento, padding }), className),
      ...props,
    }),
);
Card.displayName = "Card";

// Sub-bloques de shadcn, conservados por compatibilidad. El sistema arma la
// tarjeta con `TituloSeccion` + `padding`; no los uses en codigo nuevo.
export const CardHeader = forwardRef<HTMLDivElement, HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div ref={ref} className={cn("flex flex-col space-y-1.5 p-4", className)} {...props} />
  ),
);
CardHeader.displayName = "CardHeader";

export const CardTitle = forwardRef<HTMLDivElement, HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div
      ref={ref}
      className={cn("text-base font-semibold leading-none tracking-tight", className)}
      {...props}
    />
  ),
);
CardTitle.displayName = "CardTitle";

export const CardDescription = forwardRef<HTMLDivElement, HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div ref={ref} className={cn("text-xs text-muted-foreground", className)} {...props} />
  ),
);
CardDescription.displayName = "CardDescription";

export const CardContent = forwardRef<HTMLDivElement, HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div ref={ref} className={cn("p-4 pt-0", className)} {...props} />
  ),
);
CardContent.displayName = "CardContent";

export const CardFooter = forwardRef<HTMLDivElement, HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div ref={ref} className={cn("flex items-center p-4 pt-0", className)} {...props} />
  ),
);
CardFooter.displayName = "CardFooter";

export { cardVariants };
